import { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { InputText } from "primereact/inputtext"
import { Button } from "primereact/button"
import styles from "../../../styles/Login.module.css"

export default function ResetPasswordScreen({
  loading,
  user,
  onSubmit,
  onUserChange,
  onBack,
}) {
  // Keep direct refs for deterministic focus behavior during screen entry.
  const emailInputRef = useRef(null)
  const formRef = useRef(null)

  useEffect(() => {
    if (loading) return

    const focusInitialControl = () => {
      const inputEl = emailInputRef.current
      const hasEmail = Boolean(inputEl?.value?.trim())

      if (hasEmail) {
        const submitBtn = formRef.current?.querySelector('button[type="submit"]')
        if (submitBtn) {
          submitBtn.focus()
          return
        }
      }

      if (!inputEl) return

      inputEl.focus()
      if (!hasEmail) {
        inputEl.select()
      }
    }

    // Run immediately, then retry shortly after mount in case browser/password prompts steal focus.
    const immediateTimer = setTimeout(focusInitialControl, 0)
    const retryTimer = setTimeout(focusInitialControl, 250)

    return () => {
      clearTimeout(immediateTimer)
      clearTimeout(retryTimer)
    }
  }, [loading])

  return (
    <>
      <h2>Reset your password</h2>
      <p className={styles.subhead}>
        Enter your email to receive a link to reset your password
      </p>
      <form onSubmit={onSubmit} className="form-section" ref={formRef}>
        <div className="form-field">
          <label htmlFor="email">Email</label>
          <InputText
            id="email"
            type="email"
            ref={emailInputRef} // Using this approach because RL autoFocus seems flaky.
            value={user}
            onChange={({ target: { value } }) => onUserChange(value)}
            disabled={loading}
          />
        </div>
        <Button
          label={loading ? "Sending reset link..." : "Send Reset Link"}
          type="submit"
          className={`btn ${styles.actionBtn}`}
          loading={loading}
          disabled={loading || !user}
        />
      </form>
      <button
        className={styles["plain-text-link"]}
        onClick={onBack}
        type="button"
        disabled={loading}
      >
        Back to Login
      </button>
    </>
  )
}

ResetPasswordScreen.propTypes = {
  loading: PropTypes.bool.isRequired,
  user: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onUserChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
}
